/**
 * DrawNotificationHistoryPanel — chronological list of past notification
 * send passes for a finalized draw, plus actions to resend (failed-only
 * or all) and a drill-down view of the per-recipient outcomes for any
 * pass. Sits below the template editor inside RodeoDrawManagement.
 *
 * Resend actions are gated behind `canSend` (i.e. the draw must be
 * finalized) — the backend enforces the same constraint, but hiding
 * the buttons saves admins from clicking a button that would 400.
 */

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useDrawNotificationHistory,
  useDrawNotificationHistoryRecipients,
  useResendDrawNotifications,
  type SendHistoryEntry,
} from '@/api/extensions/useDrawOps';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  eventId: string;
  canSend?: boolean;
  className?: string;
}

const SEND_TYPE_LABEL: Record<SendHistoryEntry['send_type'], string> = {
  initial: 'Initial send',
  resend_failed: 'Resend (failed only)',
  resend_all: 'Resend (all)',
  resend_selected: 'Resend (selected)',
};

export function DrawNotificationHistoryPanel({ eventId, canSend = false, className }: Props) {
  const { showSuccess, showError } = useToast();
  const { data, isLoading, error } = useDrawNotificationHistory(eventId);
  const resendMutation = useResendDrawNotifications();

  // Drill-down panel — at most one pass open at a time so the layout
  // stays predictable. Could be loosened to multi-open later if admins
  // want to compare side-by-side.
  const [openSendId, setOpenSendId] = useState<string | null>(null);

  // Resend confirmation dialog state. Stored as a discriminated union
  // so the dialog can render mode-specific copy without prop-drilling.
  const [resendPrompt, setResendPrompt] = useState<
    { send: SendHistoryEntry; mode: 'failed' | 'all'; count: number } | null
  >(null);

  const handleResend = async () => {
    if (!resendPrompt) return;
    const prompt = resendPrompt;
    setResendPrompt(null);
    try {
      const result = await resendMutation.mutateAsync({
        eventId,
        parent_send_id: prompt.send.id,
        mode: prompt.mode,
      });
      const summary = [
        `${result.inapp_sent} in-app`,
        `${result.email_sent} email`,
        ...(result.email_failed > 0 ? [`${result.email_failed} failed`] : []),
      ].join(' · ');
      showSuccess(
        prompt.mode === 'failed' ? 'Resent to failed' : 'Resent to all',
        summary,
      );
    } catch (e: unknown) {
      showError('Resend failed', e instanceof Error ? e.message : 'Unknown error');
    }
  };

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading send history…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={className}>
        <div className="text-sm text-destructive">
          Failed to load history{error instanceof Error ? `: ${error.message}` : ''}.
        </div>
      </div>
    );
  }
  const history = data?.history ?? [];

  return (
    <div className={`space-y-2 ${className ?? ''}`}>
      <div>
        <h3 className="text-sm font-semibold">Send history</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Every dispatch (initial + resends) is logged here with per-recipient
          delivery status. Use <em>Resend failed</em> to retry just the
          recipients whose email bounced in a pass — the recipient list and
          the in-app + email pipeline both reuse the original pass's template
          unless you also edited it.
        </p>
      </div>

      {history.length === 0 ? (
        <div className="rounded-md border border-dashed bg-muted/20 p-4 text-xs text-muted-foreground">
          No notifications have been sent for this draw yet.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {history.map((h) => (
            <HistoryRow
              key={h.id}
              eventId={eventId}
              entry={h}
              canSend={canSend}
              isOpen={openSendId === h.id}
              onToggle={() => setOpenSendId(openSendId === h.id ? null : h.id)}
              onResendFailed={(send) =>
                setResendPrompt({ send, mode: 'failed', count: send.email_failed })
              }
              onResendAll={(send) =>
                setResendPrompt({ send, mode: 'all', count: send.recipient_count })
              }
              isResending={resendMutation.isPending}
            />
          ))}
        </ul>
      )}

      <Dialog open={!!resendPrompt} onOpenChange={(open) => !open && setResendPrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {resendPrompt?.mode === 'failed'
                ? `Resend to failed recipients (${resendPrompt?.count})?`
                : `Resend to all recipients (${resendPrompt?.count})?`}
            </DialogTitle>
            <DialogDescription>
              {resendPrompt?.mode === 'failed' ? (
                <>
                  This will dispatch the same notification only to recipients
                  whose email <strong>failed</strong> in the original pass.
                  In-app notifications will also be re-inserted for them.
                </>
              ) : (
                <>
                  This will dispatch the same notification to{' '}
                  <strong>every recipient</strong> from the selected pass —
                  including those who already received it successfully.
                  Use this sparingly.
                </>
              )}
              <span className="block mt-2 text-xs">
                The original pass's subject + body are reused exactly as sent.
                A new history entry is created for this resend.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResendPrompt(null)}
              disabled={resendMutation.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleResend} disabled={resendMutation.isPending}>
              {resendMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-1.5" />
              )}
              Resend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface HistoryRowProps {
  eventId: string;
  entry: SendHistoryEntry;
  canSend: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onResendFailed: (entry: SendHistoryEntry) => void;
  onResendAll: (entry: SendHistoryEntry) => void;
  isResending: boolean;
}

function HistoryRow({
  eventId,
  entry,
  canSend,
  isOpen,
  onToggle,
  onResendFailed,
  onResendAll,
  isResending,
}: HistoryRowProps) {
  const when = new Date(entry.sent_at).toLocaleString();
  const failed = entry.email_failed;
  return (
    <li className="rounded-md border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-muted/30"
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium">
              {SEND_TYPE_LABEL[entry.send_type]}
            </span>
            <span className="text-[11px] text-muted-foreground">{when}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5 flex gap-3 flex-wrap">
            <span>{entry.recipient_count} recipients</span>
            <span className="text-emerald-600 dark:text-emerald-400">
              {entry.email_sent} email sent
            </span>
            {failed > 0 && (
              <span className="text-destructive">{failed} failed</span>
            )}
            {entry.email_skipped_optout > 0 && (
              <span>{entry.email_skipped_optout} opted out</span>
            )}
            {entry.used_override && (
              <span className="text-amber-600 dark:text-amber-400">
                used override
              </span>
            )}
            {entry.sent_by_name && <span>by {entry.sent_by_name}</span>}
          </div>
        </div>
      </button>

      {isOpen && (
        <div className="border-t px-3 py-3 space-y-3">
          {canSend && (
            <div className="flex gap-2 flex-wrap">
              {failed > 0 && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => onResendFailed(entry)}
                  disabled={isResending}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Resend to {failed} failed
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => onResendAll(entry)}
                disabled={isResending}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Resend to all {entry.recipient_count}
              </Button>
            </div>
          )}
          <RecipientList eventId={eventId} sendId={entry.id} />
          <details>
            <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">
              Show subject + body that was sent
            </summary>
            <div className="mt-2 space-y-2">
              <div className="rounded border bg-muted/30 px-2 py-1.5">
                <div className="text-[10px] uppercase text-muted-foreground">Subject template</div>
                <pre className="text-xs font-mono whitespace-pre-wrap">{entry.subject_template_used}</pre>
              </div>
              <div className="rounded border bg-muted/30 px-2 py-1.5">
                <div className="text-[10px] uppercase text-muted-foreground">Body template</div>
                <pre className="text-xs font-mono whitespace-pre-wrap">{entry.body_template_used}</pre>
              </div>
            </div>
          </details>
        </div>
      )}
    </li>
  );
}

function RecipientList({ eventId, sendId }: { eventId: string; sendId: string }) {
  const { data, isLoading, error } = useDrawNotificationHistoryRecipients(eventId, sendId);
  if (isLoading) {
    return (
      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading recipients…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-destructive">
        Failed to load recipients{error instanceof Error ? `: ${error.message}` : ''}.
      </div>
    );
  }
  const recipients = data?.recipients ?? [];
  if (recipients.length === 0) {
    return (
      <div className="text-xs text-muted-foreground italic">
        No recipient rows logged (very old pass or zero-recipient send).
      </div>
    );
  }
  return (
    <div className="max-h-[260px] overflow-auto rounded border">
      <table className="w-full text-[11px]">
        <thead className="bg-muted/40 sticky top-0">
          <tr>
            <th className="text-left px-2 py-1 font-medium">Contestant</th>
            <th className="text-left px-2 py-1 font-medium">Perf · pos</th>
            <th className="text-left px-2 py-1 font-medium">In-app</th>
            <th className="text-left px-2 py-1 font-medium">Email</th>
          </tr>
        </thead>
        <tbody>
          {recipients.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="px-2 py-1">{r.display_name_at_send ?? '—'}</td>
              <td className="px-2 py-1 text-muted-foreground">
                {r.performance_name_at_send ?? '—'}
                {r.draw_position_at_send && r.draw_position_at_send !== '—'
                  ? ` · #${r.draw_position_at_send}`
                  : ''}
              </td>
              <td className="px-2 py-1">
                <StatusBadge status={r.inapp_status} />
              </td>
              <td className="px-2 py-1">
                <StatusBadge status={r.email_status} error={r.email_error} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status, error }: { status: string; error?: string | null }) {
  const cls =
    status === 'sent'
      ? 'text-emerald-600 dark:text-emerald-400'
      : status === 'failed'
        ? 'text-destructive'
        : 'text-muted-foreground';
  const Icon =
    status === 'sent'
      ? CheckCircle2
      : status === 'failed'
        ? XCircle
        : AlertCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 ${cls}`}
      title={error ?? undefined}
    >
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}
